<template>
  <ComboBox
    :id="id"
    v-model="model"
    :options="storeSetAction === undefined ? mergedOptions : options"
    icon="search"
    :loading="loading"
    :error="error || internalError"
    :server-error="serverError || internalServerError"
    @change="change"
  />
</template>
<script lang="ts" setup generic="T extends ComboBoxOption">
import {useDebounceFn} from '@vueuse/core';
import {cursor} from '@/api/paginatedResults';
import {
  type ComboBoxOption,
  type SearchComboBoxProps,
} from '@/components/shared/custom/ComboBox/ComboBox.types';

const {
  options,
  apiReadModule,
  storeSetAction = undefined,
  error = undefined,
} = defineProps<SearchComboBoxProps<T>>();

const model = defineModel<ComboBoxOption | ComboBoxOption[] | null>({required: true});

// If there is no store set action, manage the options internally
const mergedOptions = ref<T[]>([]);
onMounted(() => {
  if (storeSetAction === undefined) mergedOptions.value = options;
});

const loading = ref(false);
const internalError = ref<string>();
const internalServerError = ref<boolean>();

const read = async (value = ''): Promise<T[]> => {
  try {
    // Get the first cursor data only.
    const response = await apiReadModule(undefined, undefined, value);
    const data = cursor(response.data);
    if (value) {
      // Get the next cursor data
      const aggregatedCustomerResults: T[] =
        data.next !== null ? await apiGetPaginatedResults(apiReadModule, data.next) : [];
      // Return both the first cursor & rest of the aggregated cursors.
      return [...data.results, ...aggregatedCustomerResults];
    } else {
      return data.results; // Return the first cursor only.
    }
  } catch (error) {
    throw apiErrorMessage(error);
  }
};

const search = async (value: string) => {
  try {
    const results = await read(value);
    // Merge results with options model(i.e customersStore.customers)
    const duplicates = [...options, ...results];
    // https://stackoverflow.com/questions/2218999/how-to-remove-all-duplicates-from-an-array-of-objects
    const uniques = duplicates.filter(
      (
        e: ComboBoxOption, // element, index, array
        i: number,
        a: ComboBoxOption[],
      ) => i === a.findIndex((t: ComboBoxOption) => t.id === e.id && t.name === e.name),
    );
    if (storeSetAction !== undefined) {
      storeSetAction(uniques);
    } else {
      mergedOptions.value = uniques;
    }
  } catch (error) {
    internalServerError.value = true;
    internalError.value = String(error);
  } finally {
    loading.value = false;
  }
};

const debouncedFn = useDebounceFn(search, 1000, {maxWait: 10000});
const change = async (value: string) => {
  loading.value = true;
  await debouncedFn(value);
};
</script>
